import { useState, useEffect, useRef } from 'react';
import type { AuthProgress } from '../hooks/useBackgroundAuth';
import './LoginProgressPill.css';

/**
 * LoginProgressPill - Floating progress indicator for background auth
 *
 * Design inspired by:
 * - iOS Dynamic Island (compact floating pill)
 * - YouTube/GitHub NProgress (thin progress bar)
 * - Slack status banners (glassmorphism)
 *
 * Behavior:
 * - Delays 400ms before showing (fast auth = no indicator at all)
 * - Shows pulsing dot + status text + thin progress bar
 * - Auto-dismisses 1.8s after completion with exit animation
 * - On failure: minimizes to a subtle dot near the notch instead of disappearing
 */

const SHOW_DELAY_MS = 400;
const DONE_LINGER_MS = 1800;
const EXIT_DURATION_MS = 400;

export function LoginProgressPill({ phase, progress, message, retry }: AuthProgress) {
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [exiting, setExiting] = useState(false);
  const hasShown = useRef(false);
  const showTimer = useRef<ReturnType<typeof setTimeout>>();
  const doneTimer = useRef<ReturnType<typeof setTimeout>>();
  const exitTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(showTimer.current);
      clearTimeout(doneTimer.current);
      clearTimeout(exitTimer.current);
    };
  }, []);

  useEffect(() => {
    const isActive = phase !== 'idle' && phase !== 'done';

    // Active phase: show full pill
    if (isActive) {
      // Expand from minimized back to full pill (auth retrying)
      if (minimized) {
        setMinimized(false);
        hasShown.current = true;
        setVisible(true);
        return;
      }
      // First time: delay before showing
      if (!hasShown.current && !exiting) {
        showTimer.current = setTimeout(() => {
          hasShown.current = true;
          setVisible(true);
        }, SHOW_DELAY_MS);
        return () => clearTimeout(showTimer.current);
      }
      return;
    }

    // Done phase: show success, then fully hide (clear minimized too)
    if (phase === 'done') {
      clearTimeout(showTimer.current);
      // If was minimized, just clear the dot
      if (minimized) {
        setMinimized(false);
        return;
      }
      if (hasShown.current && !exiting) {
        setVisible(true);
        doneTimer.current = setTimeout(() => {
          setExiting(true);
          exitTimer.current = setTimeout(() => {
            setVisible(false);
            setExiting(false);
            hasShown.current = false;
          }, EXIT_DURATION_MS);
        }, DONE_LINGER_MS);
      }
      return;
    }

    // Idle phase (auth failed): minimize to notch dot instead of hiding
    if (phase === 'idle') {
      clearTimeout(showTimer.current);
      if (hasShown.current && visible && !exiting) {
        setExiting(true);
        exitTimer.current = setTimeout(() => {
          setVisible(false);
          setMinimized(true);
          setExiting(false);
        }, EXIT_DURATION_MS);
      }
    }
  }, [phase, exiting, minimized, visible]);

  // Minimized indicator near the notch - tap to retry
  if (minimized) {
    return (
      <button
        className="login-progress-mini"
        role="status"
        aria-label="Not signed in. Tap to retry."
        onClick={retry}
        type="button"
      >
        <span className="login-progress-mini__dot" />
      </button>
    );
  }

  if (!visible) return null;

  const isDone = phase === 'done';

  return (
    <div
      className={`login-progress ${exiting ? 'login-progress--exit' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className="login-progress__body">
        <span
          className={`login-progress__dot ${isDone ? 'login-progress__dot--done' : ''}`}
        />
        <span className="login-progress__text">{message}</span>
      </div>
      <div className="login-progress__bar">
        <div
          className={`login-progress__fill ${isDone ? 'login-progress__fill--done' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
